// Animation Editor Type Definitions

export type PropertyType =
  | 'position.x'
  | 'position.y'
  | 'rotation'
  | 'scale.x'
  | 'scale.y'
  | 'alpha'
  | 'tint'
  | 'sprite-frame';

export type EasingType =
  | 'linear'
  | 'ease-in'
  | 'ease-out'
  | 'ease-in-out'
  | 'ease-in-quad'
  | 'ease-out-quad'
  | 'ease-in-out-quad'
  | 'ease-in-cubic'
  | 'ease-out-cubic'
  | 'ease-in-out-cubic'
  | 'ease-in-sine'
  | 'ease-out-sine'
  | 'ease-in-out-sine'
  | 'ease-in-back'
  | 'ease-out-back'
  | 'ease-in-out-back'
  | 'ease-in-elastic'
  | 'ease-out-elastic'
  | 'ease-in-out-elastic'
  | 'ease-in-bounce'
  | 'ease-out-bounce'
  | 'ease-in-out-bounce'
  | 'custom';

export interface BezierHandle {
  x: number; // 0-1 range
  y: number; // can exceed 0-1 for overshoot
}

export interface Keyframe {
  id: string;
  time: number; // in seconds
  value: number | string; // number for transforms, string for sprite frame
  easing: EasingType;
  bezierHandles?: {
    in: BezierHandle;
    out: BezierHandle;
  };
}

export interface PropertyTrack {
  id: string;
  property: PropertyType;
  targetObjectId: string;
  keyframes: Keyframe[];
  visible: boolean;
  locked: boolean;
  color: string; // Track color for visual identification
  expanded: boolean;
}

export interface Animation {
  id: string;
  name: string;
  duration: number; // in seconds
  loop: boolean;
  loopCount: number; // 0 = infinite
  speed: number; // playback speed multiplier
  tracks: PropertyTrack[];
  createdAt: Date;
  modifiedAt: Date;
}

export interface AnimationClip {
  animationId: string;
  startTime: number;
  endTime: number;
  startFrame: number;
  endFrame: number;
}

export interface SpriteSheetAnimation {
  id: string;
  name: string;
  frames: SpriteFrame[];
  fps: number;
  loop: boolean;
}

export interface SpriteFrame {
  id: string;
  textureId: string;
  texturePath: string;
  duration: number; // in frames
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface OnionSkinSettings {
  enabled: boolean;
  showPrevious: boolean;
  showNext: boolean;
  previousCount: number;
  nextCount: number;
  previousOpacity: number;
  nextOpacity: number;
  previousTint: string;
  nextTint: string;
}

export interface TimelineViewport {
  startTime: number;
  endTime: number;
  pixelsPerSecond: number;
  scrollLeft: number;
  scrollTop: number;
}

export interface PlaybackState {
  isPlaying: boolean;
  isPaused: boolean;
  currentTime: number;
  currentFrame: number;
  fps: number;
}

export type ViewMode = 'timeline' | 'dopesheet' | 'curves';

export const PROPERTY_COLORS: Record<PropertyType, string> = {
  'position.x': '#3b82f6', // blue
  'position.y': '#22c55e', // green
  'rotation': '#eab308',   // yellow
  'scale.x': '#f97316',    // orange
  'scale.y': '#ef4444',    // red
  'alpha': '#a855f7',      // purple
  'tint': '#ec4899',       // pink
  'sprite-frame': '#f59e0b', // amber
};

export const PROPERTY_LABELS: Record<PropertyType, string> = {
  'position.x': 'Position X',
  'position.y': 'Position Y',
  'rotation': 'Rotation',
  'scale.x': 'Scale X',
  'scale.y': 'Scale Y',
  'alpha': 'Alpha',
  'tint': 'Tint',
  'sprite-frame': 'Sprite Frame',
};

export const PROPERTY_ICONS: Record<PropertyType, string> = {
  'position.x': '↔️',
  'position.y': '↕️',
  'rotation': '🔄',
  'scale.x': '↔️',
  'scale.y': '↕️',
  'alpha': '👁️',
  'tint': '🎨',
  'sprite-frame': '🖼️',
};

export const DEFAULT_FPS = 60;
export const MIN_DURATION = 0.1;
export const MAX_DURATION = 3600; // 1 hour
export const DEFAULT_DURATION = 1;

export const DEFAULT_ONION_SKIN: OnionSkinSettings = {
  enabled: false,
  showPrevious: true,
  showNext: true,
  previousCount: 3,
  nextCount: 3,
  previousOpacity: 0.3,
  nextOpacity: 0.3,
  previousTint: '#ff6b6b',
  nextTint: '#4ecdc4',
};

export const EASING_PRESETS: { type: EasingType; label: string; category: string }[] = [
  // Linear
  { type: 'linear', label: 'Linear', category: 'Basic' },

  // Standard
  { type: 'ease-in', label: 'Ease In', category: 'Standard' },
  { type: 'ease-out', label: 'Ease Out', category: 'Standard' },
  { type: 'ease-in-out', label: 'Ease In Out', category: 'Standard' },

  // Quad
  { type: 'ease-in-quad', label: 'Ease In Quad', category: 'Quadratic' },
  { type: 'ease-out-quad', label: 'Ease Out Quad', category: 'Quadratic' },
  { type: 'ease-in-out-quad', label: 'Ease In Out Quad', category: 'Quadratic' },

  // Cubic
  { type: 'ease-in-cubic', label: 'Ease In Cubic', category: 'Cubic' },
  { type: 'ease-out-cubic', label: 'Ease Out Cubic', category: 'Cubic' },
  { type: 'ease-in-out-cubic', label: 'Ease In Out Cubic', category: 'Cubic' },

  // Sine
  { type: 'ease-in-sine', label: 'Ease In Sine', category: 'Sine' },
  { type: 'ease-out-sine', label: 'Ease Out Sine', category: 'Sine' },
  { type: 'ease-in-out-sine', label: 'Ease In Out Sine', category: 'Sine' },

  // Back
  { type: 'ease-in-back', label: 'Ease In Back', category: 'Back' },
  { type: 'ease-out-back', label: 'Ease Out Back', category: 'Back' },
  { type: 'ease-in-out-back', label: 'Ease In Out Back', category: 'Back' },

  // Elastic
  { type: 'ease-in-elastic', label: 'Ease In Elastic', category: 'Elastic' },
  { type: 'ease-out-elastic', label: 'Ease Out Elastic', category: 'Elastic' },
  { type: 'ease-in-out-elastic', label: 'Ease In Out Elastic', category: 'Elastic' },

  // Bounce
  { type: 'ease-in-bounce', label: 'Ease In Bounce', category: 'Bounce' },
  { type: 'ease-out-bounce', label: 'Ease Out Bounce', category: 'Bounce' },
  { type: 'ease-in-out-bounce', label: 'Ease In Out Bounce', category: 'Bounce' },

  // Custom
  { type: 'custom', label: 'Custom Bezier', category: 'Custom' },
];
